import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { formatDuration, formatApiError } from '../../lib/utils';
import { Plus, Edit2, Trash2, Headphones, Clock, Coins, Music, Globe, Shield, Eye, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'it', name: 'Italian' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'ar', name: 'Arabic' }
];

const CATEGORIES = ['Museum', 'Historical Site', 'Church', 'Gallery', 'Stadium', 'Walking Tour', 'Food Tour', 'Neighborhood'];

export default function AdminAttractions() {
    const [attractions, setAttractions] = useState([]);
    const [cities, setCities] = useState([]);
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAttraction, setEditingAttraction] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        city_id: '',
        category: '',
        featured_image: '',
        opening_hours: '',
        languages: ['en'],
        audio_duration: 30,
        credits_required: 1,
        is_active: true,
        assigned_operator_id: ''
    });

    useEffect(() => {
        Promise.all([fetchAttractions(), fetchCities(), fetchOperators()]);
    }, []);

    const fetchAttractions = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/attractions`, { withCredentials: true });
            setAttractions(response.data);
        } catch (error) {
            toast.error('Failed to fetch attractions');
        } finally {
            setLoading(false);
        }
    };

    const fetchCities = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/cities`, { withCredentials: true });
            setCities(response.data);
        } catch (error) {
            console.error('Failed to fetch cities');
        }
    };

    const fetchOperators = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/operators`, { withCredentials: true });
            setOperators(response.data.filter(op => op.is_whitelabel));
        } catch (error) {
            console.error('Failed to fetch operators');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...formData,
                languages: formData.languages.filter(l => l.trim()).map(l => l.toLowerCase()),
                audio_duration: parseInt(formData.audio_duration) || 30,
                credits_required: parseInt(formData.credits_required) || 1,
                assigned_operator_id: formData.assigned_operator_id || null
            };

            if (editingAttraction) {
                await axios.put(`${API}/api/admin/attractions/${editingAttraction.id}`, data, { withCredentials: true });
                toast.success('Attraction updated successfully');
            } else {
                await axios.post(`${API}/api/admin/attractions`, data, { withCredentials: true });
                toast.success('Attraction created successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchAttractions();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const handleDelete = async (attractionId) => {
        if (!window.confirm('Are you sure you want to delete this attraction?')) return;
        try {
            await axios.delete(`${API}/api/admin/attractions/${attractionId}`, { withCredentials: true });
            toast.success('Attraction deleted successfully');
            fetchAttractions();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const handleDuplicate = async (attraction) => {
        if (!window.confirm(`Duplicate "${attraction.title}" with all text, images, and audio tracks? You can then reassign it to a white-label operator and swap the audio files.`)) return;
        try {
            const res = await axios.post(`${API}/api/admin/attractions/${attraction.id}/duplicate`, {}, { withCredentials: true });
            toast.success(res.data.message);
            fetchAttractions();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail) || 'Duplication failed');
        }
    };

    const openEditDialog = (attraction) => {
        setEditingAttraction(attraction);
        setFormData({
            title: attraction.title,
            description: attraction.description,
            city_id: attraction.city_id,
            category: attraction.category,
            featured_image: attraction.featured_image || '',
            opening_hours: attraction.opening_hours || '',
            languages: (attraction.languages || ['en']).map(l => l.toLowerCase()),
            audio_duration: attraction.audio_duration || 30,
            credits_required: attraction.credits_required || 1,
            is_active: attraction.is_active,
            assigned_operator_id: attraction.assigned_operator_id || ''
        });
        setDialogOpen(true);
    };

    const resetForm = () => {
        setEditingAttraction(null);
        setFormData({
            title: '',
            description: '',
            city_id: '',
            category: '',
            featured_image: '',
            opening_hours: '',
            languages: ['en'],
            audio_duration: 30,
            credits_required: 1,
            is_active: true,
            assigned_operator_id: ''
        });
    };

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-attractions">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Attractions</h1>
                        <p className="text-muted-foreground mt-1">Manage audio guide attractions</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="add-attraction-btn">
                                <Plus className="w-4 h-4" /> Add Attraction
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#121620] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-white font-outfit">
                                    {editingAttraction ? 'Edit Attraction' : 'Add New Attraction'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>Title</Label>
                                    <Input
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="Colosseum Audio Guide"
                                        required
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="attraction-title-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>City</Label>
                                        <Select value={formData.city_id} onValueChange={(value) => setFormData({ ...formData, city_id: value })}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="attraction-city-select">
                                                <SelectValue placeholder="Select city" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                {cities.map((city) => (
                                                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Category</Label>
                                        <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="attraction-category-select">
                                                <SelectValue placeholder="Select category" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                {CATEGORIES.map((cat) => (
                                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="A detailed description of the attraction..."
                                        rows={3}
                                        required
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="attraction-description-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Featured Image URL</Label>
                                    <Input
                                        value={formData.featured_image}
                                        onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                                        placeholder="https://..."
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Duration (min)</Label>
                                        <Input
                                            type="number"
                                            value={formData.audio_duration}
                                            onChange={(e) => setFormData({ ...formData, audio_duration: e.target.value })}
                                            min={1}
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Credits Required</Label>
                                        <Input
                                            type="number"
                                            value={formData.credits_required}
                                            onChange={(e) => setFormData({ ...formData, credits_required: e.target.value })}
                                            min={1}
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Opening Hours</Label>
                                        <Input
                                            value={formData.opening_hours}
                                            onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
                                            placeholder="9:00 - 18:00"
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Languages</Label>
                                    <div className="flex flex-wrap gap-2 p-3 rounded-md bg-[#0A0D14] border border-white/10 min-h-[44px]">
                                        {LANGUAGES.map(lang => {
                                            const isSelected = formData.languages.includes(lang.code);
                                            return (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    onClick={() => {
                                                        if (isSelected) {
                                                            if (formData.languages.length > 1) {
                                                                setFormData({ ...formData, languages: formData.languages.filter(l => l !== lang.code) });
                                                            }
                                                        } else {
                                                            setFormData({ ...formData, languages: [...formData.languages, lang.code] });
                                                        }
                                                    }}
                                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                                        isSelected 
                                                            ? 'bg-gold/20 text-gold border border-gold/40' 
                                                            : 'bg-white/5 text-muted-foreground border border-white/10 hover:border-white/20'
                                                    }`}
                                                    data-testid={`lang-chip-${lang.code}`}
                                                >
                                                    {lang.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-xs text-muted-foreground">Select all languages this attraction supports. Audio tracks will be grouped by language.</p>
                                </div>
                                {/* Exclusive Operator Assignment */}
                                {operators.length > 0 && (
                                    <div className="space-y-2 p-3 rounded-lg bg-gold/5 border border-gold/20">
                                        <Label className="flex items-center gap-2">
                                            <Shield className="w-4 h-4 text-gold" />
                                            Assign to White-Label Operator (Exclusive)
                                        </Label>
                                        <Select
                                            value={formData.assigned_operator_id || 'none'}
                                            onValueChange={(v) => setFormData({ ...formData, assigned_operator_id: v === 'none' ? '' : v })}
                                        >
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="assign-operator-select">
                                                <SelectValue placeholder="Public (all travelers)" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10">
                                                <SelectItem value="none">Public (all travelers)</SelectItem>
                                                {operators.map(op => (
                                                    <SelectItem key={op.id} value={op.id}>{op.company_name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">Exclusive attractions only appear for this operator's travelers. Leave as 'Public' for all.</p>
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <Label>Active</Label>
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="bg-gold text-[#0A0D14] hover:bg-gold/90" data-testid="attraction-submit-btn">
                                        {editingAttraction ? 'Update' : 'Create'} Attraction
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Attractions Table */}
                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground">Attraction</TableHead>
                                    <TableHead className="text-muted-foreground">City</TableHead>
                                    <TableHead className="text-muted-foreground">Category</TableHead>
                                    <TableHead className="text-muted-foreground">Languages</TableHead>
                                    <TableHead className="text-muted-foreground">Duration</TableHead>
                                    <TableHead className="text-muted-foreground">Credits</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {attractions.map((attraction) => (
                                    <TableRow key={attraction.id} className="border-white/10 hover:bg-white/5">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {attraction.featured_image ? (
                                                    <img src={attraction.featured_image} alt={attraction.title} className="w-12 h-12 rounded-md object-cover" />
                                                ) : (
                                                    <div className="w-12 h-12 rounded-md bg-gold/10 flex items-center justify-center">
                                                        <Headphones className="w-6 h-6 text-gold" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-white font-medium">{attraction.title}</p>
                                                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">{attraction.description}</p>
                                                    {attraction.assigned_operator_id && (
                                                        <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[10px] rounded bg-gold/10 text-gold border border-gold/20">
                                                            <Shield className="w-2.5 h-2.5" />
                                                            Exclusive: {attraction.assigned_operator_name || 'WL Operator'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">{attraction.city_name}</TableCell>
                                        <TableCell>
                                            <span className="px-2 py-1 rounded-full text-xs bg-white/5 text-muted-foreground">
                                                {attraction.category}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 flex-wrap">
                                                {(attraction.languages || ['en']).map(lang => (
                                                    <span key={lang} className="px-1.5 py-0.5 rounded text-[10px] bg-gold/10 text-gold uppercase font-medium">
                                                        {lang}
                                                    </span>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <Clock className="w-4 h-4" />
                                                <span>{formatDuration(attraction.audio_duration)}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-gold">
                                                <Coins className="w-4 h-4" />
                                                <span>{attraction.credits_required}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs ${attraction.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                                {attraction.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Link to={`/admin/attractions/${attraction.id}/audio`}>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="text-gold hover:text-gold/80"
                                                        data-testid={`manage-audio-${attraction.id}`}
                                                    >
                                                        <Music className="w-4 h-4" />
                                                    </Button>
                                                </Link>
                                                <Link to={`/admin/attractions/${attraction.id}/ar`}>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="text-purple-400 hover:text-purple-300"
                                                        data-testid={`manage-ar-${attraction.id}`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </Link>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => openEditDialog(attraction)}
                                                    className="text-muted-foreground hover:text-white"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => handleDuplicate(attraction)}
                                                    className="text-blue-400 hover:text-blue-300"
                                                    title="Duplicate"
                                                    data-testid={`duplicate-attraction-${attraction.id}`}
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => handleDelete(attraction.id)}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {attractions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                            No attractions found. Add your first attraction to get started.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
